import {
	Box,
	Button,
	Container,
	Grid,
	Paper,
	Typography,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Checkbox,
	Select,
	MenuItem,
	FormControl
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import * as locales from 'date-fns/locale';
import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import i18next, {getBrowserLanguage} from '../../lang';
import { addBusinessDays } from 'date-fns';
import ConfirmDialog from "../../components/ConfirmDialog";
import axios from 'axios';
import {rootStore} from "../../stores/RootStore";
import PurchaseStepper from "../../components/PurchaseStepper";
import { DeliveryAddress, DeliveryDateTimeData } from './interfaces';

interface LocationState {
	selectedAddresses: DeliveryAddress[];
	prevDeliveryData?: { [key: string]: DeliveryDateTimeData };
	prevOrderData?: {
		selectedCurriculums: any[];
		filters: {
			categoryId: number;
		};
	};
	lockedAddressIds?: number[];
	fromOrderConfirm?: boolean;
	hasTempOrder?: boolean;
}

export const getTimeSlots = () => [
	{ id: 1, time: i18next.t('purchase.morning') },
	{ id: 2, time: i18next.t('purchase.time_slot_14_16') },
	{ id: 3, time: i18next.t('purchase.time_slot_16_18') },
	{ id: 4, time: i18next.t('purchase.time_slot_18_20') },
	{ id: 5, time: i18next.t('purchase.time_slot_19_21') }
];

export const timeSlots = getTimeSlots();

const DeliveryDateTime: React.FC = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const { authStore } = rootStore;
	const [isCancelOrderDialogOpen, setIsCancelOrderDialogOpen] = useState(false);
	const locationState = location.state as LocationState | undefined;
	const [tempOrder, setTempOrder] = useState<any>(null);
	const [isTempOrderDialogOpen, setIsTempOrderDialogOpen] = useState(false);
	const [addresses, setAddresses] = useState<DeliveryAddress[]>(locationState?.selectedAddresses || []);
	const { prevDeliveryData, prevOrderData, lockedAddressIds, fromOrderConfirm, hasTempOrder: hasTempOrderFromState } = locationState || {};
	// この画面自体に tempOrder があるか、前の画面から引き継いだフラグのOR
	const hasTempOrder = !!tempOrder || !!hasTempOrderFromState;
	const [deliveryData, setDeliveryData] = useState<{ [key: string]: DeliveryDateTimeData }>(() => {
		if (prevDeliveryData) {
			const restoredData: { [key: string]: DeliveryDateTimeData } = {};
			Object.entries(prevDeliveryData).forEach(([key, value]) => {
				if (value.date && addresses.some(addr => addr.addressId?.toString() === key)) {
					restoredData[key] = {
						date: new Date(value.date),
						time: value.time
					};
				}
			});
			return restoredData;
		}
		return {};
	});

	const [selectedItems, setSelectedItems] = useState<number[]>(addresses.map(address => address.addressId));

	useEffect(() => {
		if (location.state?.tempOrder) {
			setTempOrder(location.state.tempOrder);
			setIsTempOrderDialogOpen(true);
			// state 초기화 (뒤로가기 시 state가 유지되는 것을 방지)
			window.history.replaceState({}, document.title);
		}
	}, [location.state]);

	const fetchClassroomInfo = useCallback(async () => {
		try {
			const response = await axios.get(`/api/v1/orders/addresses/${authStore.user.id}`);
			if (response.data) {
				setAddresses(response.data);
				setSelectedItems(response.data.map((address: DeliveryAddress) => address.addressId));
			}
		} catch (error: any) {
			console.error('Failed to fetch delivery address:', error);
		}
	}, [authStore.user.id]);

	useEffect(() => {
		if (!locationState?.selectedAddresses || locationState.selectedAddresses.length === 0) {
			fetchClassroomInfo();
		}
	}, [locationState?.selectedAddresses, fetchClassroomInfo]);

	const handleDateChange = (addressId: number | string, date: Date | null) => {
		setDeliveryData(prev => ({
			...prev,
			[addressId]: {
				date: date,
				time: prev[addressId]?.time || null
			}
		}));
	};

	const handleTimeSlotChange = (addressId: number | string, timeSlot: number) => {
		setDeliveryData(prev => ({
			...prev,
			[addressId]: {
				...prev[addressId],
				time: timeSlot
			}
		}));
	};

	const handleCheckboxChange = (addressId: number) => {
		setSelectedItems(prev => {
			if (prev.includes(addressId)) {
				return prev.filter(id => id !== addressId);
			} else {
				return [...prev, addressId];
			}
		});
	};

	const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
		if (event.target.checked) {
			setSelectedItems(addresses.map(address => address.addressId));
		} else {
			setSelectedItems([]);
		}
	};

	const minSelectableDate = React.useMemo(() => {
		return new Date();
	}, []);

	const maxSelectableDate = React.useMemo(() => {
		const d = new Date();
		d.setFullYear(d.getFullYear() + 1);
		return d;
	}, []);

	const getDisabledDates = () => {
		const today = new Date();
		const disabledDate = addBusinessDays(today, 5);
		return (date: Date) => {
			return date <= disabledDate;
		};
	};

	const handleNext = () => {
		// selectedItems と addresses が非同期更新でずれた場合に undefined を除外する
		const processedData = selectedItems
			.map(addrId => addresses.find(addr => addr.addressId === addrId))
			.filter((address): address is DeliveryAddress => address !== undefined)
			.map(address => ({
				...address,
				// addressId を一意キーとして使用（同一ユーザーの複数住所を区別するため）
				id: String(address.addressId),
				// 元の DeliveryAddress.id はユーザーID（商品マッピング API 用）
				userId: String(address.id),
				deliveryDate: deliveryData[address.addressId]?.date || null,
				deliveryTime: deliveryData[address.addressId]?.time || null
			}));

		navigate('/purchase/order-input', {
			state: {
				deliveryData: processedData,
				prevOrderData,
				fromOrderConfirm,
				hasTempOrder
			}
		});

	};

	const handleCancelTempOrder = async () => {
		try {
			await axios.delete(`/api/v1/orders/temp`);
		} catch (error) {
			console.error('Failed to delete temp order:', error);
		}

		setIsTempOrderDialogOpen(false);
	}

	const handleCancelOrder = (tempOrderDeleted = false) => {
		const effectiveHasTempOrder = tempOrderDeleted ? false : hasTempOrder;
		setIsCancelOrderDialogOpen(false);
		if (fromOrderConfirm && prevOrderData) {
			navigate('/purchase/order-confirm', {
				state: {
					orderData: {
						deliveryData: (prevOrderData as any).deliveryData,
						orderQuantities: (prevOrderData as any).orderQuantities
					},
					products: (prevOrderData as any).products,
					productSets: (prevOrderData as any).productSets,
					categories: (prevOrderData as any).categories,
					userProductMappings: (prevOrderData as any).userProductMappings,
					hasTempOrder: effectiveHasTempOrder
				}
			});
		} else if (authStore.user.level === 'CLASSROOM_MANAGER') {
			navigate('/purchase/delivery-datetime', { state: { hasTempOrder: effectiveHasTempOrder } });
		} else {
			navigate('/purchase/delivery-address', { state: { hasTempOrder: effectiveHasTempOrder } });
		}
	};

	const handleCancelOrderWithDeleteTemp = async () => {
		try {
			await axios.delete('/api/v1/orders/temp');
		} catch (error) {
			console.error('Failed to delete temp order:', error);
		}
		handleCancelOrder(true);
	};

	return (
		<Container maxWidth={false} sx={{ mt: 4, mb: 4, width: '100%' }}>
			<PurchaseStepper activeStep={1} />
			<Grid container spacing={3}>
				<Grid item xs={12}>
					<Box sx={{ mb: 3 }}>
						<Typography variant="h6" gutterBottom>
							{i18next.t('purchase.select_delivery_date')}
						</Typography>
						<Grid container spacing={3} sx={{ mb: 2, height: '60px' }}>
							<Grid item xs={12}>
								<Grid container spacing={1} justifyContent="flex-end" sx={{ height: '100%' }}>
									<Grid item sm={3}>
									</Grid>
									<Grid item>
									</Grid>
								</Grid>
							</Grid>
						</Grid>
					</Box>

					<Box sx={{
						display: 'flex',
						flexDirection: 'column',
						mt: '20px'
					}}>
						<Paper sx={{
							display: 'flex',
							flexDirection: 'column',
							boxShadow: 'none',
							border: 'none',
							borderRadius: '4px',
							backgroundColor: '#fff'
						}}>
							<TableContainer sx={{ overflowX: 'auto' }}>
								<Table sx={{
									minWidth: 650,
									'& .MuiTableCell-root': {
										borderRight: '1px solid #f0f0f0',
										borderBottom: '1px solid #f0f0f0',
										padding: '0 8px',
										height: '40px'
									}
								}} size="small">
									<TableHead>
										<TableRow>
											<TableCell padding="checkbox" sx={{
												backgroundColor: '#f5f5f5',
												width: '3% !important',
												height: '40px !important'
											}}>
												<Checkbox
													size="small"
													indeterminate={selectedItems.length > 0 && selectedItems.length < addresses.length}
													checked={selectedItems.length === addresses.length}
													onChange={handleSelectAllClick}
													sx={{
														color: '#bdbdbd',
														'&.Mui-checked': {
															color: '#2196f3'
														},
														padding: '0 0 0 10px'
													}}
												/>
											</TableCell>
											<TableCell sx={{
												backgroundColor: '#f5f5f5',
												fontSize: '0.7rem',
												fontWeight: 800,
												width: '3% !important',
												textAlign: 'center',
												height: '40px !important'
											}}>
												{i18next.t('common.number')}
											</TableCell>
											<TableCell sx={{
												backgroundColor: '#f5f5f5',
												fontSize: '0.7rem',
												fontWeight: 800,
												width: '200px',
												textAlign: 'center',
												height: '40px !important'
											}}>
												{i18next.t('order.classroom')}
											</TableCell>
											<TableCell sx={{
												backgroundColor: '#f5f5f5',
												fontSize: '0.7rem',
												fontWeight: 800,
												width: '300px',
												textAlign: 'center',
												height: '40px !important'
											}}>
												{i18next.t('address.address')}
											</TableCell>
											<TableCell sx={{
												backgroundColor: '#f5f5f5',
												fontSize: '0.7rem',
												fontWeight: 800,
												width: '130px',
												textAlign: 'center',
												height: '40px !important'
											}}>
												{i18next.t('contact.phone')}
											</TableCell>
											<TableCell sx={{
												backgroundColor: '#f5f5f5',
												fontSize: '0.7rem',
												fontWeight: 800,
												width: '130px',
												textAlign: 'center',
												height: '40px !important'
											}}>
												{i18next.t('purchase.person_in_charge')}
											</TableCell>
											<TableCell sx={{
												backgroundColor: '#f5f5f5',
												fontSize: '0.7rem',
												fontWeight: 800,
												width: '130px',
												textAlign: 'center',
												height: '40px !important'
											}}>
												{i18next.t('order.delivery_date')}
												<Typography variant="caption" sx={{ display: 'block', color: 'red', fontSize: '0.6rem', fontWeight: 400, lineHeight: 1.3, mt: 0.5 }}>
													{i18next.t('purchase.earliest_delivery_note')}
												</Typography>
											</TableCell>
											<TableCell sx={{
												backgroundColor: '#f5f5f5',
												fontSize: '0.7rem',
												fontWeight: 800,
												width: '130px',
												textAlign: 'center',
												height: '40px !important'
											}}>
												{i18next.t('order.time_slot')}
											</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{addresses.sort((a, b) => String(a.id).localeCompare(String(b.id))).map((address, index) => (
											<TableRow
												key={address.addressId}
												sx={{
													height: '40px !important',
													backgroundColor: selectedItems.includes(address.addressId) ? '#FFFFD0' : 'inherit',
													'&:hover': {
														backgroundColor: selectedItems.includes(address.addressId) ? '#FFFFD0' : '#f5f5f5'
													}
												}}
											>
												<TableCell padding="checkbox" sx={{ height: '40px !important' }}>
													<Checkbox
														size="small"
														checked={selectedItems.includes(address.addressId)}
														onChange={() => handleCheckboxChange(address.addressId)}
														sx={{
															color: '#bdbdbd',
															'&.Mui-checked': {
																color: '#2196f3'
															},
															padding: '0 0 0 10px'
														}}
													/>
												</TableCell>
												<TableCell sx={{ fontSize: '0.7rem', textAlign: 'center', height: '40px !important' }}>
													{index + 1}
												</TableCell>
												<TableCell sx={{ fontSize: '0.7rem', textAlign: 'center', height: '40px !important' }}>
													{address.classroomName}
												</TableCell>
												<TableCell sx={{ fontSize: '0.7rem', height: '40px !important' }}>
													{address.address}
												</TableCell>
												<TableCell sx={{ fontSize: '0.7rem', textAlign: 'center', height: '40px !important' }}>
													{address.phone}
												</TableCell>
												<TableCell sx={{ fontSize: '0.7rem', textAlign: 'center', height: '40px !important' }}>
													{address.userName}
												</TableCell>
												<TableCell sx={{ height: '40px !important' }}>
													<LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={locales[getBrowserLanguage()] as any}>
														<DatePicker
															value={deliveryData[address.addressId]?.date || null}
															onChange={(newValue) => handleDateChange(address.addressId, newValue)}
															shouldDisableDate={getDisabledDates()}
															minDate={minSelectableDate}
															maxDate={maxSelectableDate}
															slotProps={{
																textField: {
																	size: 'small',
																},
															}}
															sx={{
																width: '100%',
																'& .MuiInputBase-root': {
																	height: '35px',
																	fontSize: '0.8rem'
																}
															}}
														/>
													</LocalizationProvider>
												</TableCell>
												<TableCell sx={{ height: '40px !important' }}>
													<FormControl fullWidth>
														<Select
															value={deliveryData[address.addressId]?.time || ''}
															onChange={(e) => handleTimeSlotChange(address.addressId, Number(e.target.value))}
															size="small"
															sx={{
																fontSize: '0.8rem',
																height: '35px'
															}}
														>
															{timeSlots.map((slot) => (
																<MenuItem
																	key={slot.id}
																	value={slot.id}
																	sx={{ fontSize: '0.8rem' }}
																>
																	{slot.time}
																</MenuItem>
															))}
														</Select>
													</FormControl>
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</TableContainer>
							<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
								<Typography sx={{ fontSize: '0.7rem', color: '#666', ml: 1 }}>
									TOTAL
								</Typography>
								<Typography sx={{ fontSize: '0.7rem', color: '#666', mr: 1 }}>
									{selectedItems.length} / {addresses.length}
								</Typography>
							</Box>
							<Box sx={{
								p: 1,
								backgroundColor: '#fff',
								borderTop: '1px solid #e0e0e0',
								display: 'flex',
								justifyContent: 'space-between',
								alignItems: 'center',
								height: '35px',
								flexShrink: 0
							}}>
								<Box style={{marginTop: 20, display: 'flex', justifyContent: 'flex-start'}}>
									{authStore.user.level !== 'CLASSROOM_MANAGER' &&
										<Button
											variant="outlined"
											sx={{
												fontSize: '0.8rem',
												height: '28px',
												minWidth: '80px',
												mr: 1
											}}
											onClick={() => navigate('/purchase/delivery-address', {
												state: {
													selectedAddressIds: [...selectedItems, ...(lockedAddressIds || [])],
													prevDeliveryData: Object.entries(deliveryData).reduce((acc, [key, value]) => ({
														...acc,
														[key]: {
															date: value.date?.toISOString() || null,
															time: value.time
														}
													}), {}),
													lockedAddressIds,
													prevOrderData,
													fromOrderConfirm: false,
													hasTempOrder
												}
											})}
										>
											{i18next.t('purchase.go_to_delivery_address')}
										</Button>
									}
								</Box>
								<Box sx={{
									p: 1,
									backgroundColor: '#fff',
									borderTop: '1px solid #e0e0e0',
									display: 'flex',
									justifyContent: 'flex-end',
									alignItems: 'center',
									height: '35px',
									flexShrink: 0
								}}>
									{authStore.user.level !== 'CLASSROOM_MANAGER' &&
										<Box style={{marginTop: 20, display: 'flex', justifyContent: 'flex-start'}}>
											<Button
												variant="outlined"
												sx={{
													fontSize: '0.8rem',
													height: '28px',
													minWidth: '80px',
													mr: 1
												}}
												onClick={() => setIsCancelOrderDialogOpen(true)}
											>
												{fromOrderConfirm ? i18next.t('purchase.cancel_and_return_to_order_confirm') : i18next.t('order.order_cancelled')}
											</Button>
										</Box>
									}
									<Box style={{marginTop: 20, display: 'flex', justifyContent: 'flex-start'}}>
										<Button
											variant="contained"
											disabled={selectedItems.length === 0}
											sx={{
												fontSize: '0.8rem',
												height: '28px',
												minWidth: '80px',
												mr: 1,
												backgroundColor: 'primary.main',
												'&:hover': {
													backgroundColor: 'primary.dark'
												}
											}}
											onClick={handleNext}
										>
											{i18next.t('purchase.go_to_order_input')}
										</Button>
									</Box>
								</Box>
							</Box>
						</Paper>
					</Box>
				</Grid>
			</Grid>
			<ConfirmDialog
				open={isTempOrderDialogOpen}
				onClose={handleCancelTempOrder}
				onConfirm={() => navigate('/purchase/order-input', { state: { tempOrder } })}
				title={i18next.t('purchase.save_temporarily')}
				message={i18next.t('purchase.has_saved_order')}
				button1Text={i18next.t('common.cancel')}
				button2Text={i18next.t('common.confirm')}
			/>
			<ConfirmDialog
				open={isCancelOrderDialogOpen}
				onClose={() => setIsCancelOrderDialogOpen(false)}
				onConfirm={handleCancelOrder}
				title={fromOrderConfirm ? i18next.t('purchase.cancel_and_return_to_order_confirm') : i18next.t('order.order_cancelled')}
				message={fromOrderConfirm ? i18next.t('purchase.confirm_cancel_add_delivery') : i18next.t('common.confirm_cancel')}
				button1Text={i18next.t('common.no')}
				button2Text={i18next.t('common.yes')}
				onConfirmAlt={hasTempOrder ? handleCancelOrderWithDeleteTemp : undefined}
				confirmAltText={hasTempOrder ? i18next.t('purchase.yes_delete_temp_order') : undefined}
				confirmAltColor="error"
			/>
		</Container>
	);
};

export default DeliveryDateTime;